import React from 'react';
import { PromotionPayload } from '../../../api/generated';
import { appSettings } from '../../../config/appSettings';

/**
 * Formats a time of day string (HH:mm:ss) to HH:mm string for display
 */
const formatTime = (time: string | null | undefined): string => {
  if (!time) return '';
  // time is in "HH:mm:ss" format, extract "HH:mm"
  const parts = time.split(':');
  if (parts.length < 2) return '';
  return `${parts[0]}:${parts[1]}`;
};

/**
 * Formats a date string to a readable format in Copenhagen timezone
 */
const formatDate = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'Europe/Copenhagen',
    });
  } catch {
    return '';
  }
};

/**
 * Formats a datetime string to a readable format with time in Copenhagen timezone
 */
const formatDateTime = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Copenhagen',
    });
  } catch {
    return '';
  }
};

/**
 * Formats a promotion object into a human-readable JSX description.
 * Main line shows cashback info, constraints shown as bullet list below.
 */
export const formatPromotionText = (promotion: PromotionPayload): React.ReactNode => {
  const constraints: React.ReactNode[] = [];
  const oisterPercent = appSettings.OISTER_COMMISSION_PERCENT;

  // Customer cashback (main line)
  const customerPercent = promotion.customerCashbackPercent ?? 0;
  const totalCommission = customerPercent + oisterPercent;

  // Date constraints
  const startDate = formatDateTime(promotion.startDateTime);
  const endDate = formatDateTime(promotion.endDateTime);
  if (startDate && endDate) {
    constraints.push(<li key="dates">Promotion starts on {startDate} and ends on {endDate}</li>);
  } else if (startDate) {
    constraints.push(<li key="dates">Promotion starts on {startDate}</li>);
  } else if (endDate) {
    constraints.push(<li key="dates">Promotion ends on {endDate}</li>);
  }

  // Time of day constraints
  const minTime = formatTime(promotion.minTimeOfDay);
  const maxTime = formatTime(promotion.maxTimeOfDay);
  if (minTime && maxTime) {
    constraints.push(<li key="time">Only between {minTime} and {maxTime}</li>);
  } else if (minTime) {
    constraints.push(<li key="time">Only after {minTime}</li>);
  } else if (maxTime) {
    constraints.push(<li key="time">Only before {maxTime}</li>);
  }

  // Transaction amount constraints
  const minAmount = promotion.minTransactionAmount;
  const maxAmount = promotion.maxTransactionAmount;
  if (minAmount !== null && minAmount !== undefined && maxAmount !== null && maxAmount !== undefined) {
    constraints.push(<li key="amount">Only when order amount is between {minAmount} DKK and {maxAmount} DKK</li>);
  } else if (minAmount !== null && minAmount !== undefined) {
    constraints.push(<li key="amount">Only when order amount is greater than {minAmount} DKK</li>);
  } else if (maxAmount !== null && maxAmount !== undefined) {
    constraints.push(<li key="amount">Only when order amount is less than {maxAmount} DKK</li>);
  }

  return (
    <div>
      <div>
        <strong>{customerPercent}%</strong> cashback for customer, <strong>{oisterPercent}%</strong> for OiSTER Bonus (<strong>{totalCommission}%</strong> commission in total)
      </div>
      {constraints.length > 0 && (
        <ul className="list-disc list-inside mt-1 text-gray-500">
          {constraints}
        </ul>
      )}
    </div>
  );
};
